import { useFragment, useMutation } from "react-relay";
import { graphql } from "babel-plugin-relay/macro";
import { Card } from "primereact/card";
import React from "react";
import { Dropdown } from "primereact/dropdown";
import { useFormik } from "formik";
import { Button } from "primereact/button";
import { AdvertisementForm_TreeNodeFragment$key } from "../../../../../../__generated__/AdvertisementForm_TreeNodeFragment.graphql";
import { AdvertisementForm_SetAdvertisementConfigurationV2Mutation } from "../../../../../../__generated__/AdvertisementForm_SetAdvertisementConfigurationV2Mutation.graphql";
import { ValidatedField } from "../../../../core/form/ValidatedField";
import {
	addEditedFormToEditedFormsArray,
	resetArrayOfEditedForms,
} from "../../../../../redux/slices/CoreSlice";
import { useTypedDispatch } from "../../../../../redux/Store";
import { TREE_I18N_KEY, TREE_I18N_MAP } from "../../../../../../lms-admin-impl/i18n/tree.i18n";

const TREE_NODE_FRAGMENT = graphql`
	fragment AdvertisementForm_TreeNodeFragment on TreeNode {
		id
		structureDefinition {
			... on RootStructureDefinition {
				extension {
					... on RootExtensionImpl {
						advertisementConfig {
							isFeatured
						}
					}
				}
			}
		}
	}
`;

const SET_ADVERTISEMENT_CONFIGURATION_MUTATION = graphql`
	mutation AdvertisementForm_SetAdvertisementConfigurationV2Mutation(
		$input: SetAdvertisementConfigurationV2Input!
	) {
		Admin {
			Tree {
				setAdvertisementConfiguration(input: $input) {
					rootNode {
						...AdvertisementForm_TreeNodeFragment
						...TreeContainer_TreeNodeFragment
					}
				}
			}
		}
	}
`;

interface FormState {
	isFeatured: boolean;
}

interface OwnProps {
	treeNodeFragmentRef: AdvertisementForm_TreeNodeFragment$key;
}

export const AdvertisementForm = ({ treeNodeFragmentRef }: OwnProps) => {
	const rootNode = useFragment<AdvertisementForm_TreeNodeFragment$key>(
		TREE_NODE_FRAGMENT,
		treeNodeFragmentRef,
	);
	const dispatch = useTypedDispatch();
	const formId = "AdvertisementForm";
	const [setAdvertisementConfiguration, isSettingAdvertisementConfiguration] =
		useMutation<AdvertisementForm_SetAdvertisementConfigurationV2Mutation>(
			SET_ADVERTISEMENT_CONFIGURATION_MUTATION,
		);

	const formik = useFormik<FormState>({
		enableReinitialize: true,
		initialValues: {
			isFeatured:
				rootNode.structureDefinition.extension?.advertisementConfig?.isFeatured || false,
		},
		onSubmit: (values, { setSubmitting }) => {
			setAdvertisementConfiguration({
				variables: {
					input: {
						rootNodeId: rootNode.id,
						isFeatured: values.isFeatured,
					},
				},
				onCompleted: () => {
					dispatch(resetArrayOfEditedForms());
				},
			});
			setSubmitting(false);
		},
	});

	return (
		<Card className="mb-2">
			<h2>Bewerbung des {TREE_I18N_MAP(TREE_I18N_KEY.treeAttributiveGenitive)}</h2>

			<form onSubmit={formik.handleSubmit} className="p-fluid">
				<ValidatedField<FormState, boolean>
					name={"isFeatured"}
					label={"Ist gefeaturet"}
					component={({ fieldName, fieldValue, updateField }) => {
						return (
							<div>
								<Dropdown
									name={fieldName}
									value={fieldValue}
									options={[
										{
											label: `Ja, hebe ${TREE_I18N_MAP(
												TREE_I18N_KEY.thisTree,
											)} besonders hervor.`,
											value: true,
										},
										{
											label: `Nein, hebe ${TREE_I18N_MAP(
												TREE_I18N_KEY.thisTree,
											)} nicht hervor.`,
											value: false,
										},
									]}
									onChange={(e) => {
										updateField(e.value);
										dispatch(
											addEditedFormToEditedFormsArray({ form: formId! }),
										);
									}}
								/>
							</div>
						);
					}}
					formikConfig={formik}
				/>
				<Button
					disabled={
						Object.entries(formik.touched).length === 0 ||
						isSettingAdvertisementConfiguration
					}
					type="submit"
					label="Speichern"
					className="p-mt-2"
				/>
			</form>
		</Card>
	);
};
